import { Trash2, X, CheckSquare, Square } from "lucide-react";

/**
 * Barre d'actions en bulk pour les tables/cartes admin.
 * Affiche le nombre d'éléments sélectionnés et un bouton "Supprimer la sélection".
 *
 * Props:
 *  - count       : number — nombre d'éléments sélectionnés
 *  - total       : number — total visible (pour "Tout sélectionner")
 *  - onClear     : () => void — vider la sélection
 *  - onSelectAll : () => void — sélectionner tout (optionnel)
 *  - onDelete    : () => void — déclenche la suppression bulk
 *  - label       : string  — libellé contextuel (ex: "événement", "membre")
 *  - testId      : string  — préfixe data-testid
 *  - extraActions: ReactNode — boutons supplémentaires (archiver…)
 */
export function BulkActionsBar({ count, total = 0, onClear, onSelectAll, onDelete, label = "élément", testId = "bulk", extraActions = null }) {
  if (count === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-rose-200 bg-rose-50/60 px-4 py-2.5" data-testid={`${testId}-bar`}>
      <div className="flex items-center gap-3 text-sm font-bold text-rose-800">
        <CheckSquare className="h-4 w-4" />
        <span data-testid={`${testId}-count`}>{count} {label}{count > 1 ? "s" : ""} sélectionné{count > 1 ? "s" : ""}</span>
        {onSelectAll && total > count && (
          <button onClick={onSelectAll} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-rose-700 hover:bg-rose-100" data-testid={`${testId}-select-all`}>
            Tout sélectionner ({total})
          </button>
        )}
        <button onClick={onClear} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-600 hover:bg-neutral-100" data-testid={`${testId}-clear`}>
          <X className="h-3 w-3" />Effacer
        </button>
      </div>
      <div className="flex items-center gap-2">
        {extraActions}
        <button onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-rose-700" data-testid={`${testId}-delete`}>
          <Trash2 className="h-3.5 w-3.5" /> Supprimer la sélection
        </button>
      </div>
    </div>
  );
}

/**
 * Checkbox réutilisable (carte ou cellule de table) — style standardisé.
 */
export function BulkCheckbox({ checked, onChange, testId, className = "" }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className={`grid h-5 w-5 place-items-center rounded ${checked ? "bg-rose-500 text-white" : "bg-white border border-neutral-300 text-transparent hover:border-rose-400"} ${className}`}
      data-testid={testId}
      aria-checked={checked}
      role="checkbox"
    >
      {checked ? <CheckSquare className="h-4 w-4" /> : <Square className="h-3 w-3" />}
    </button>
  );
}
